import React, { useEffect, useState } from "react";
import { Bar, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart } from "recharts";

import api from "../../../../services/api";

export default ({ values, projectId }) => {
  const [activities, setActivities] = useState([]);
  const [banks, setBanks] = useState([]);

  const fetchActivities = async () => {
    try {
      const { ok, data } = await api.post(`/activity/search`, { projectId, budgetId: values._id });
      if (!ok) return;
      setActivities(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBanks = async () => {
    try {
      const { ok, data } = await api.post(`/bank/search`, { projectId, budgetId: values._id, per_page: 20000 });
      if (!ok) return;
      setBanks((data?.banks || []).filter((bank) => bank.category !== "INCOME"));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchBanks();
  }, [values._id]);

  const monthlyData = calculateSingleBudgetMonthlyData(values, activities, banks);
  const activitiesByUser = buildActivitiesByUser(activities);
  const banksByName = buildBanksByName(banks);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-300 rounded p-4">
        <h3 className="text-lg font-semibold mb-4">Cumulative expenses over time</h3>
        <BudgetChart monthlyData={monthlyData} />
      </div>

      <div className="bg-white border border-gray-300 rounded p-4">
        <h3 className="text-lg font-semibold mb-4">Detailed expenses</h3>
        <ActivitiesAndBanks activitiesByUser={activitiesByUser} banksByName={banksByName} />
      </div>
    </div>
  );
};

const BudgetChart = ({ monthlyData }) => (
  <div className="h-64 w-full">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={monthlyData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis tickFormatter={(value) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", notation: "compact" }).format(value)} />
        <RechartsTooltip formatter={(value, name) => [new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value), name]} />
        <Legend />
        <Bar dataKey="cumulativeActual" name="Cumulative Actual Expenses" fill="#3B82F6" />
        <Bar dataKey="cumulativeExpected" name="Cumulative Expected Budget" fill="#9CA3AF" />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const ActivitiesAndBanks = ({ activitiesByUser, banksByName }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
    <div>
      <h4 className="text-md font-medium mb-2">Activities by User</h4>
      <div className="space-y-2">
        {activitiesByUser.length === 0 ? (
          <p className="text-gray-400 text-sm">No activities</p>
        ) : (
          activitiesByUser.map((u) => (
            <div key={u.userName} className="flex items-center justify-between p-2 bg-white rounded-md border">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                  {u.userAvatar ? <img src={u.userAvatar} alt={u.userName} className="w-8 h-8 rounded-full" /> : u.userName.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <div className="text-sm">
                  <div className="font-medium">{u.userName}</div>
                  <div className="text-gray-400 text-xs">{u.userJobTitle || "No position"}</div>
                </div>
              </div>
              <div className="text-sm text-right">
                <div className="font-medium">{u.totalCost.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
                <div className="text-gray-400 text-xs">{u.totalDays.toFixed(2)} days</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    <div>
      <h4 className="text-md font-medium mb-2">Bank Expenses</h4>
      <div className="space-y-2">
        {banksByName.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-400 text-sm">No bank expenses</p>
            <p className="text-gray-300 text-xs">All expenses via activities</p>
            <p className="text-gray-400 text-sm font-medium mt-1">0 €</p>
          </div>
        ) : (
          banksByName.map((bank) => (
            <div key={bank.name} className="flex items-center justify-between p-2 bg-white rounded-md border">
              <div className="flex items-center space-x-2">
                <span>💳</span>
                <div className="text-sm">
                  <div>{bank.name}</div>
                  <div className="text-gray-400 text-xs">{bank.count} transaction{bank.count > 1 ? "s" : ""}</div>
                </div>
              </div>
              <div className="text-sm font-medium">{bank.totalAmount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);

function calculateSingleBudgetMonthlyData(budget, activities, banks) {
  const allMonths = new Set();
  const currentDate = new Date();
  const start = new Date(budget.startAt);
  const end = new Date(budget.endAt);

  for (let date = new Date(start); date <= end; date.setMonth(date.getMonth() + 1)) {
    allMonths.add(new Date(date).getTime());
  }
  activities.forEach((a) => { const d = new Date(a.date); allMonths.add(new Date(d.getFullYear(), d.getMonth(), 1).getTime()); });
  banks.forEach((b) => { const d = new Date(b.date); allMonths.add(new Date(d.getFullYear(), d.getMonth(), 1).getTime()); });

  const sortedMonths = Array.from(allMonths).sort((a, b) => a - b).map((t) => new Date(t));

  const totalAmount = budget.amount || 0;
  const startDate = new Date(budget.startAt); startDate.setDate(1); startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(budget.endAt); endDate.setHours(23, 59, 59, 999);
  const totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1;
  const monthlyExpected = totalAmount / totalMonths;

  return sortedMonths.reduce((acc, monthDate, index) => {
    const isInFuture = monthDate > currentDate;
    const monthString = monthDate.toLocaleString("default", { month: "long", year: "numeric" });

    const monthActivities = activities.filter((a) => {
      const d = new Date(a.date);
      return d.getMonth() === monthDate.getMonth() && d.getFullYear() === monthDate.getFullYear();
    });
    const monthBanks = banks.filter((b) => {
      const d = new Date(b.date);
      return d.getMonth() === monthDate.getMonth() && d.getFullYear() === monthDate.getFullYear();
    });

    const actual = isInFuture ? 0 : monthActivities.reduce((sum, a) => sum + Math.abs(a.value), 0) + monthBanks.reduce((sum, b) => sum + Math.abs(b.amount), 0);

    const monthStart = new Date(monthDate); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    const isWithinRange = monthStart >= startDate && monthStart <= endDate;

    const previousCumulative = index > 0 ? acc[index - 1].cumulativeActual : 0;
    const cumulativeActual = isInFuture ? 0 : previousCumulative + actual;

    let cumulativeExpected = 0;
    if (isWithinRange) {
      const monthsFromStart = (monthDate.getFullYear() - startDate.getFullYear()) * 12 + (monthDate.getMonth() - startDate.getMonth()) + 1;
      cumulativeExpected = monthlyExpected * monthsFromStart;
    }

    return [...acc, { month: monthString, cumulativeActual, cumulativeExpected }];
  }, []);
}

function buildActivitiesByUser(activities) {
  const map = {};
  for (const activity of activities) {
    const userName = activity.userName || "Unknown User";
    if (!map[userName]) {
      map[userName] = { userName, userAvatar: activity.userAvatar, totalCost: 0, totalDays: 0, userJobTitle: activity.userJobTitle };
    }
    map[userName].totalCost += Math.abs(activity.value);
    map[userName].totalDays += activity.total / 8;
  }
  return Object.values(map).sort((a, b) => b.totalCost - a.totalCost);
}

function buildBanksByName(banks) {
  const filtered = (banks || []).filter((b) => b.category !== "INCOME" && b.category !== "DISBURSEMENT");
  const map = {};
  for (const bank of filtered) {
    const name = bank.name || "Unknown";
    if (!map[name]) map[name] = { name, totalAmount: 0, count: 0 };
    map[name].totalAmount += Math.abs(bank.amount);
    map[name].count += 1;
  }
  return Object.values(map).sort((a, b) => b.totalAmount - a.totalAmount);
}
